import { AvailabilityStatusEnum, SubmissionStatusEnum, WindowStatusEnum } from '@/api';

import type {
  BadgeVariantKey,
  VendorAvailabilityData,
  VendorAvailabilityListItem,
} from './types';
import { SubmissionState } from './types';
import dayjs from 'dayjs';

export type ListItemStatus = {
  isSubmitted: boolean;
  isNotSubmitted: boolean;
  isLocked: boolean;
  isSubmissionRequired: boolean;
  isMissedDeadline: boolean;
  isActionRequired: boolean;
};

// Single source of truth for availability list-item state. Every consumer
// (status text, status color, badge variant, status icon) reads from here so
// the UI stays in sync when the rules drift.
export const getListItemStatus = (item: VendorAvailabilityListItem): ListItemStatus => {
  const isSubmitted = item.submissionStatus === SubmissionStatusEnum.SUBMITTED;
  const isNotSubmitted = item.submissionStatus === SubmissionStatusEnum.NOT_SUBMITTED;
  const isLocked = item.windowStatus === WindowStatusEnum.LOCKED;
  const isSubmissionRequired = item.requiresSubmission;
  const isMissedDeadline = isSubmissionRequired && isNotSubmitted && isLocked;
  const isActionRequired =
    isSubmissionRequired && item.availabilityStatus === AvailabilityStatusEnum.ACTION_REQUIRED;

  return {
    isSubmitted,
    isNotSubmitted,
    isLocked,
    isSubmissionRequired,
    isMissedDeadline,
    isActionRequired,
  };
};

export const getStatusText = (item: VendorAvailabilityListItem): string => {
  const { isSubmitted, isSubmissionRequired, isMissedDeadline, isActionRequired } =
    getListItemStatus(item);

  const submittedOn = dayjs(item.submission?.submittedAt).format('MMM D');

  if (isMissedDeadline) {
    return 'Availability not submitted';
  }
  if (isActionRequired) {
    return `Availability required \u00b7 Due ${dayjs(item.deadlineDate).format('MMM D')}`;
  }
  if (isSubmitted && !isSubmissionRequired) {
    return `Availability updated on ${submittedOn}`;
  }
  if (isSubmitted && isSubmissionRequired) {
    return `Availability submitted on ${submittedOn}`;
  }
  if (!isSubmissionRequired) {
    return 'Current availability applies';
  }

  return '';
};

export const deriveSubmissionState = (data: VendorAvailabilityData): SubmissionState => {
  if (data.windowStatus === WindowStatusEnum.LOCKED) return SubmissionState.PAST_CUTOFF;
  if (data.submissionStatus === SubmissionStatusEnum.SUBMITTED) return SubmissionState.SUBMITTED;
  return SubmissionState.NOT_SUBMITTED;
};

export const getVariantKey = (item: VendorAvailabilityListItem): BadgeVariantKey | null => {
  const { isSubmitted, isNotSubmitted, isLocked, isSubmissionRequired } = getListItemStatus(item);

  if (isSubmissionRequired) {
    if (isLocked && isNotSubmitted) return 'NOT_SUBMITTED_LOCKED';
    if (isSubmitted) return 'SUBMITTED';
    if (isNotSubmitted && !isLocked) return 'ACTION_REQUIRED';
    return null;
  }

  return isSubmitted ? 'UPDATED' : 'NO_SUBMISSION_REQUIRED';
};

export const getStatusColor = (item: VendorAvailabilityListItem): string => {
  const { isMissedDeadline, isActionRequired } = getListItemStatus(item);

  if (isMissedDeadline) return 'red';
  if (isActionRequired) return '#986801';
  return 'teal';
};
